import type {
  AggregateExternalCosts,
  EnrollmentSessionFormAssessmentCard,
  FinancingModeBreakdownItem,
  TrainingPlanIndividualTimeslots,
} from "@simbelapp/order-sdk";
import { useState } from "#imports";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { useProjectsStore } from "~/store/project/projects.store";
import { ItemType } from "~/utils/enums/common.enums";
import { useRequestsStore } from "~/store/requests/requests.store";
import { displayFinancingModeName } from "~/utils/financing-modes/financing-mode-label";
import { filterAncillaryExternalCosts } from "~/composables/external-cost/useExternalCost";

interface ISessionHeading {
  firstname: string | null;
  lastname: string | null;
  fullname: string | null;
  email: string | null;
  status: string | null;
  training: string | null;
  partner: string | null;
  linkedToRequest: boolean | null;
}

interface ISessionRecap {
  type: string | null;
  startDate: string | null;
  endDate: string | null;
  format: string | null;
  location: string | null;
  priceExclTax: number | null;
  attendedHours: number | null;
  trainingDuration: number | null;
  otherCosts: AggregateExternalCosts[] | null;
  salaryCost: number | null;
  satisfaction: number | null;
  status: string | null;
  financing: { name: string; amount: number }[];
  designation: string | null;
  trainingType?: string | null;
  timeslots: TrainingPlanIndividualTimeslots[] | null;
  trainers_salary_costs?: number | null;
  score?: number | null;
  // à chaud satisfaction-survey recipientId → deep-links the participant's survey (/evaluations?recipientId=…).
  // Null for projects and for attendees with no à chaud survey recipient.
  recipientId?: string | null;
  // The participant's questionnaires on the session, with their score; a draft's participant has none yet.
  assessments: EnrollmentSessionFormAssessmentCard[];
}

interface IUserInfos {
  userId: string | null;
  job: string | null;
  email: string | null;
  managerEmail: string | null;
  team: string | null;
  matricule: string | null;
  mainEntity: string | null;
  site: string | null;
}

interface IHistory {
  step: number;
  type: string;
}

interface LoadSessionIndividual {
  detailsId: string | null;
  source: string | null;
}

type SourceType = "project" | "session";

// SDK 8.0.0 returns the per-mode breakdown directly — the recap lists every funded mode
// (name + amount), no longer just the 3 legacy ones. Read-only, so no legacy bridge needed.
const buildRecapFinancing = (
  modes: FinancingModeBreakdownItem[] | null | undefined,
): { name: string; amount: number }[] =>
  (modes ?? [])
    .map((mode) => ({ name: displayFinancingModeName(mode), amount: Number(mode.priceExclTax) }))
    .filter((mode) => Number.isFinite(mode.amount) && mode.amount > 0);

export const useSessionIndividual = () => {
  const sourceType = useState<SourceType | null>("sourceType", () => null);
  const sourceId = useState<string | null>("sourceId", () => null);
  const userId = useState<string | null>("userId", () => null);
  const status = useState<string | null>("status", () => null);
  const sessionStore = useSessionsStore();
  const projectStore = useProjectsStore();
  const router = useRouter();

  async function loadSessionIndividual(params: LoadSessionIndividual, changeUrl = true) {
    const { detailsId, source } = params;

    if (detailsId && source === "project") {
      sourceType.value = "project";
      await projectStore.fetchProjectIndividual(detailsId);
      sourceId.value = projectStore.trainingPlanProjectIndividual?.projectId || null;
      userId.value = projectStore.trainingPlanProjectIndividual?.userId || null;
      status.value = sessionStore.trainingPlanSessionIndividual?.status || null;

      if (changeUrl) await router.push({ query: { details_id: detailsId, type: source } });
    } else if (detailsId && source === "session") {
      sourceType.value = "session";
      await sessionStore.fetchTrainingPlanSession(detailsId);
      sourceId.value = sessionStore.trainingPlanSessionIndividual?.sessionId || null;
      userId.value = sessionStore.trainingPlanSessionIndividual?.userId || null;
      status.value = sessionStore.trainingPlanSessionIndividual?.status || null;

      if (changeUrl) await router.push({ query: { details_id: detailsId, type: source } });
    }
    sessionStore.showSessionModal = true;
  }

  function getSessionHeading(): ISessionHeading {
    const sessionDetails = sessionStore?.trainingPlanSessionIndividual;
    const projectDetails = projectStore?.trainingPlanProjectIndividual;

    let objectBase: ISessionHeading = {
      firstname: null,
      lastname: null,
      fullname: null,
      email: null,
      status: null,
      training: null,
      partner: null,
      linkedToRequest: false,
    };

    if (sourceType.value === "project") {
      objectBase = {
        ...objectBase,
        firstname: projectDetails?.firstName ?? null,
        lastname: projectDetails?.lastName ?? null,
        fullname:
          projectDetails?.firstName && projectDetails.lastName
            ? `${projectDetails?.firstName} ${projectDetails.lastName}`
            : null,
        email: projectDetails?.employeeEmail ?? null,
        status: projectDetails?.status ?? null,
        training: projectDetails?.training ?? null,
        partner: projectDetails?.trainingPartner ?? null,
        linkedToRequest: projectDetails?.sourceType === ItemType.REQUEST,
      };
    } else if (sourceType.value === "session") {
      objectBase = {
        ...objectBase,
        firstname: sessionDetails?.firstName ?? null,
        lastname: sessionDetails?.lastName ?? null,
        fullname:
          sessionDetails?.firstName && sessionDetails.lastName
            ? `${sessionDetails?.firstName} ${sessionDetails.lastName}`
            : null,
        email: sessionDetails?.employeeEmail ?? null,
        status: sessionDetails?.status ?? null,
        training: sessionDetails?.training ?? null,
        partner: sessionDetails?.trainingPartner ?? null,
      };
    }

    return objectBase;
  }

  function getSessionRecap(): ISessionRecap {
    const sessionDetails = sessionStore?.trainingPlanSessionIndividual;
    const projectDetails = projectStore?.trainingPlanProjectIndividual;

    let objectBase: ISessionRecap = {
      type: null,
      startDate: null,
      endDate: null,
      format: null,
      location: null,
      priceExclTax: null,
      attendedHours: null,
      trainingDuration: null,
      satisfaction: null,
      status: null,
      financing: [],
      designation: null,
      salaryCost: null,
      otherCosts: null,
      trainingType: null,
      timeslots: null,
      score: null,
      recipientId: null,
      assessments: [],
    };

    if (sourceType.value === "project") {
      objectBase = {
        ...objectBase,
        type: projectDetails?.type ?? null,
        trainingType: projectDetails?.trainingType ?? null,
        startDate: projectDetails?.startDate ?? null,
        endDate: projectDetails?.endDate ?? null,
        format: projectDetails?.format ?? null,
        location: projectDetails?.location ?? null,
        designation: projectDetails?.designation ?? null,
        priceExclTax: projectDetails?.priceExclTax ?? null,
        attendedHours: null,
        trainingDuration: projectDetails?.trainingDuration ?? null,
        satisfaction: null,
        status: projectDetails?.status ?? null,
        financing: buildRecapFinancing(projectDetails?.financingModes),
        otherCosts: filterAncillaryExternalCosts(projectDetails?.externalCosts),
        salaryCost: projectDetails?.salaryCost != null ? Number(projectDetails.salaryCost) : null,
        timeslots: projectDetails?.timeslots ?? null,
      };
    } else if (sourceType.value === "session") {
      objectBase = {
        ...objectBase,
        trainingType: sessionDetails?.trainingType ?? null,
        type: sessionDetails?.type ?? null,
        startDate: sessionDetails?.startDate ?? null,
        endDate: sessionDetails?.endDate ?? null,
        format: sessionDetails?.format ?? null,
        location: sessionDetails?.location ?? null,
        designation: sessionDetails?.designation ?? null,
        priceExclTax: Number(sessionDetails?.priceExclTax) ?? null,
        attendedHours: sessionDetails?.attendedHours ?? null,
        trainingDuration: sessionDetails?.trainingDuration ?? null,
        satisfaction: sessionDetails?.satisfactionOverall ?? null,
        status: sessionDetails?.status ?? null,
        financing: buildRecapFinancing(sessionDetails?.financingModes),
        salaryCost: sessionDetails?.salaryCost != null ? Number(sessionDetails.salaryCost) : null,
        otherCosts: filterAncillaryExternalCosts(sessionDetails?.externalCosts),
        timeslots: sessionDetails?.timeslots ?? null,
        score: sessionDetails?.score ?? null,
        recipientId: sessionDetails?.recipientId ?? null,
        assessments: sessionDetails?.assessments ?? [],
      };
    }
    return objectBase;
  }

  function getUserInfos(): IUserInfos {
    const sessionDetails = sessionStore?.trainingPlanSessionIndividual;
    const projectDetails = projectStore?.trainingPlanProjectIndividual;

    let objectBase: IUserInfos = {
      userId: null,
      email: null,
      managerEmail: null,
      job: null,
      team: null,
      matricule: null,
      mainEntity: null,
      site: null,
    };

    if (sourceType.value === "project") {
      objectBase = {
        ...objectBase,
        userId: projectDetails?.userId ?? null,
        email: projectDetails?.employeeEmail ?? null,
        managerEmail: projectDetails?.managerEmail ?? null,
        matricule: projectDetails?.employeeNumber ?? null,
        job: projectDetails?.job ?? null,
        team: projectDetails?.team ?? null,
        mainEntity: projectDetails?.legalEntity ?? null,
        site: projectDetails?.site ?? null,
      };
    } else if (sourceType.value === "session") {
      objectBase = {
        ...objectBase,
        userId: sessionDetails?.userId ?? null,
        email: sessionDetails?.employeeEmail ?? null,
        managerEmail: sessionDetails?.managerEmail ?? null,
        job: sessionDetails?.job ?? null,
        team: sessionDetails?.team ?? null,
        matricule: sessionDetails?.employeeNumber ?? null,
        mainEntity: sessionDetails?.legalEntity ?? null,
        site: sessionDetails?.site ?? null,
      };
    }
    return objectBase;
  }

  function getSessionHistory(): IHistory {
    const sessionDetails = sessionStore?.trainingPlanSessionIndividual;

    const objectBase: IHistory = {
      step: 0,
      type: "",
    };

    if (sourceType.value === "project") {
      objectBase.step = sessionDetails?.step ?? 1; // TODO project step
      objectBase.type = "project";
    } else if (sourceType.value === "session") {
      objectBase.step = sessionDetails?.step ?? 0;
      if (sessionDetails?.requestId) objectBase.type = "request";
      else if (sessionDetails?.bookingId) objectBase.type = "booking";
    }

    return objectBase;
  }

  function resetSessionIndividual() {
    sourceType.value = null;
    sourceId.value = null;
    userId.value = null;
    status.value = null;
  }

  async function getRequestInfos(sourceType: SourceType) {
    let details;
    if (sourceType === ItemType.SESSION) {
      details = sessionStore?.trainingPlanSessionIndividual;
    } else {
      details = projectStore?.trainingPlanProjectIndividual;
    }

    if (details?.requestId) {
      const requestsStore = useRequestsStore();
      await requestsStore.fetchRequestDetails(details?.requestId ?? "");

      const requestInfos = {
        requestId: requestsStore.requestDetails?.requestId,
        createdAt: requestsStore.requestDetails?.createdAt,
        status: requestsStore.requestDetails?.status,
        managerPriority: requestsStore.requestDetails?.managerPriority,
      };
      return requestInfos;
    }
    return null;
  }

  return {
    status,
    sourceType,
    sourceId,
    userId,
    getSessionHeading,
    getSessionRecap,
    getSessionHistory,
    getRequestInfos,
    getUserInfos,
    loadSessionIndividual,
    resetSessionIndividual,
  };
};
